<script setup lang="ts">
import { storeToRefs } from "pinia";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import { useProgrammingPlanningStore } from "@/stores/piloting/programming-planning-store";
import $date, { dayMapping, EDateScaling } from "@/utils/date";
import { pad } from "@/utils/number";

const { t, k, d } = useTypedIntl();

const programmingPlanningStore = useProgrammingPlanningStore();
const { planningRangeScale, months, days } = storeToRefs(programmingPlanningStore);
</script>

<template>
  <div
    class="flex w-full h-[50px]"
    :data-test-id="DATA_TEST_IDS.components.planningDateRangeHeaderCell.component"
    :data-range-mode="planningRangeScale"
  >
    <template v-if="planningRangeScale === EDateScaling.QUARTER">
      <div
        v-for="month in months"
        :data-test-id="DATA_TEST_IDS.components.planningDateRangeHeaderCell.quarterMonthItem"
        :data-month-number="$date.getDateMonth(month.monthStartDate)"
        :key="$date.formatLocalDate(month.monthStartDate)"
        class="item"
        :style="{
          width: `${month.proportion * 100}%`,
        }"
      >
        <small class="font-semibold capitalize">{{ d(month.monthStartDate, { month: "long" }) }}</small>
      </div>
    </template>
    <template v-else-if="planningRangeScale === EDateScaling.MONTH">
      <div
        v-for="day in days"
        :data-test-id="DATA_TEST_IDS.components.planningDateRangeHeaderCell.monthDayItem"
        :data-day="$date.formatLocalDate(day)"
        :key="$date.formatLocalDate(day)"
        class="item min-w-[18px] flex-1"
      >
        <small class="font-semibold text-[10px]">{{ day.getDate() }}</small>
      </div>
    </template>
    <template v-else-if="planningRangeScale === EDateScaling.DAY">
      <div
        v-for="i in 24"
        class="item flex-1"
      >
        <small class="font-semibold text-[10px]">{{ pad(`${i - 1}`) }}H</small>
      </div>
    </template>
    <template v-else>
      <div
        v-for="day in days"
        :data-test-id="DATA_TEST_IDS.components.planningDateRangeHeaderCell.weekDayItem"
        :data-day="$date.formatLocalDate(day)"
        :key="$date.formatLocalDate(day)"
        class="item px-2 flex-1"
      >
        <small class="text-[10px] uppercase font-light"
          >{{ t(k.global.weekDays[dayMapping[$date.getDateWeekDay(day)]]).substring(0, 3) }}.</small
        >
        <small class="font-semibold">{{ day.getDate() }}</small>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.item {
  @apply flex flex-col items-center justify-center leading-tight;

  &:not(:last-of-type) {
    box-shadow: inset -1px 0 #f0f0f4;
  }
}
</style>
